<template>
  <div class="landing-layout">
    <!-- Landing Header -->
    <header class="landing-header">
      <div class="landing-header-content">
        <router-link
          to="/"
          class="landing-logo-link"
          aria-label="NovelWriter2 - Go to home"
        >
          <img
            src="/images/logo-full.svg"
            alt="NovelWriter2"
            class="landing-logo"
          />
        </router-link>

        <nav class="landing-nav" role="navigation" aria-label="Main navigation">
          <router-link
            to="/features"
            class="landing-nav-link"
          >
            Features
          </router-link>
          <router-link
            to="/pricing"
            class="landing-nav-link"
          >
            Pricing
          </router-link>
          <router-link
            to="/login"
            class="btn-secondary"
          >
            Log In
          </router-link>
          <router-link
            to="/signup"
            class="btn-primary"
          >
            Sign Up
          </router-link>
        </nav>
      </div>
    </header>

    <!-- Landing Content -->
    <main class="landing-main">
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>

    <!-- Landing Footer -->
    <footer class="landing-footer">
      <div class="landing-footer-content">
        <div class="landing-footer-links">
          <a href="/about" class="landing-footer-link">About</a>
          <a href="/privacy" class="landing-footer-link">Privacy</a>
          <a href="/terms" class="landing-footer-link">Terms</a>
          <a href="/contact" class="landing-footer-link">Contact</a>
        </div>
        <p class="landing-copyright">
          © {{ new Date().getFullYear() }} NovelWriter2. All rights reserved.
        </p>
      </div>
    </footer>
  </div>
</template>

<script setup>
// No complex logic needed for landing layout
// Router view handles all the content transitions
</script>

<style scoped>
.landing-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, var(--color-cream) 0%, var(--color-bg-secondary) 100%);
}

.landing-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 72px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--color-border-light);
  z-index: var(--z-sticky);
}

.landing-header-content {
  max-width: 1200px;
  margin: 0 auto;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--space-2xl);
}

.landing-logo-link {
  display: flex;
  align-items: center;
  text-decoration: none;
  transition: opacity var(--transition-fast);
}

.landing-logo-link:hover {
  opacity: 0.8;
}

.landing-logo {
  height: 40px;
  width: auto;
}

.landing-nav {
  display: flex;
  align-items: center;
  gap: var(--space-xl);
}

.landing-nav-link {
  color: var(--color-text-secondary);
  text-decoration: none;
  font-weight: var(--font-weight-medium);
  transition: color var(--transition-fast);
}

.landing-nav-link:hover,
.landing-nav-link:focus-visible {
  color: var(--color-text-primary);
}

.landing-main {
  flex: 1;
  margin-top: 72px; /* Header height */
}

.landing-footer {
  padding: var(--space-3xl) var(--space-2xl);
  background: var(--color-bg-primary);
  border-top: 1px solid var(--color-border-light);
}

.landing-footer-content {
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
}

.landing-footer-links {
  display: flex;
  justify-content: center;
  gap: var(--space-2xl);
  margin-bottom: var(--space-xl);
}

.landing-footer-link {
  color: var(--color-text-secondary);
  text-decoration: none;
  font-size: var(--font-size-sm);
  transition: color var(--transition-fast);
}

.landing-footer-link:hover,
.landing-footer-link:focus-visible {
  color: var(--color-text-primary);
}

.landing-copyright {
  color: var(--color-text-tertiary);
  font-size: var(--font-size-xs);
  margin: 0;
}

/* Fade transition for page changes */
.fade-enter-active,
.fade-leave-active {
  transition: opacity var(--transition-base);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Responsive design - Mobile first */
@media (max-width: 767px) {
  .landing-header {
    height: 64px;
  }

  .landing-header-content {
    padding: 0 var(--space-lg);
  }

  .landing-logo {
    height: 32px;
  }

  .landing-nav {
    gap: var(--space-lg);
  }

  .landing-nav-link {
    display: none; /* Hide nav links on mobile */
  }

  .btn-secondary,
  .btn-primary {
    font-size: var(--font-size-xs);
    padding: var(--space-sm) var(--space-md);
  }

  .landing-main {
    margin-top: 64px;
  }

  .landing-footer {
    padding: var(--space-2xl) var(--space-lg);
  }

  .landing-footer-links {
    flex-direction: column;
    gap: var(--space-lg);
    margin-bottom: var(--space-lg);
  }

  .landing-copyright {
    font-size: 11px;
  }
}

@media (min-width: 768px) and (max-width: 1023px) {
  .landing-header-content {
    padding: 0 var(--space-xl);
  }

  .landing-nav-link {
    font-size: var(--font-size-sm);
  }
}

@media (min-width: 1024px) {
  .landing-header-content {
    padding: 0 var(--space-3xl);
  }

  .landing-footer-links {
    gap: var(--space-3xl);
  }
}
</style>
